<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm border-radius-xl border-0">
          <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center bg-transparent gap-3">
            <h6 class="font-weight-bolder mb-0">{{ branchPluralLabel }} Management</h6>
            <div class="d-flex flex-wrap align-items-center gap-2 w-100 w-md-auto justify-content-between justify-content-md-end">
              <div class="btn-group btn-group-sm shadow-none">
                <button class="btn btn-sm mb-0" :class="viewMode === 'table' ? 'btn-theme-primary' : 'btn-outline-theme'" @click="viewMode = 'table'">
                  <i class="fas fa-table"></i>
                </button>
                <button class="btn btn-sm mb-0" :class="viewMode === 'card' ? 'btn-theme-primary' : 'btn-outline-theme'" @click="viewMode = 'card'">
                  <i class="fas fa-th-large"></i>
                </button>
              </div>
              <button class="btn btn-outline-secondary btn-sm mb-0" @click="showFilters = !showFilters">
                <i class="fas" :class="showFilters ? 'fa-times' : 'fa-search'"></i>
                {{ showFilters ? ' Close' : ' Search' }}
              </button>
              <!-- UPDATED: Button now hidden for operator/opertor roles -->
              <button v-if="userRole !== 'operator' && userRole !== 'opertor'" class="btn btn-theme-primary btn-sm px-3 mb-0 flex-grow-1 flex-md-grow-0" @click="openAddModal">
                <i class="fas fa-plus me-1"></i> {{ addBranchLabel }}
              </button>
            </div>
          </div>

          <TableSearchLimit :search="globalSearch" @update:search="globalSearch = $event" :limit="limit" @update:limit="limit = $event" @refresh="fetchBranches" />

          <div class="card-body px-0 pt-0 pb-2">
            <!-- Loading Indicator -->
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-xs text-secondary mt-2">Loading {{ branchPluralLabel.toLowerCase() }}...</p>
            </div>

            <!-- 1. TABLE VIEW -->
            <div v-else-if="viewMode === 'table'" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">{{ branchLabel }} ID</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ nameFieldLabel }}</th>
                    <th v-if="!isMedicalType" class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">City</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Assigned Doctors</th>
                    <th v-if="!isMedicalType" class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Role</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredBranches.length === 0">
                    <td :colspan="isSuperAdmin ? (isMedicalType ? 6 : 7) : (isMedicalType ? 5 : 6)" class="text-center py-5 text-secondary">
                      <i class="fas fa-building fa-2x mb-2 text-secondary opacity-5 d-block"></i>
                      <p class="text-sm font-weight-bold mb-1">No {{ branchPluralLabel }} Found</p>
                      <p class="text-xs text-muted mb-0">Click "+ {{ addBranchLabel }}" above to create your first {{ branchLabel.toLowerCase() }}.</p>
                    </td>
                  </tr>
                  <template v-else v-for="branch in filteredBranches" :key="branch.id">
                    <tr @click="toggleExpand(branch.id)" class="branch-main-row" :class="{ 'active-row-theme': expandedId === branch.id }">
                      <td class="ps-4">
                        <div class="d-flex align-items-center">
                          <div class="expand-icon-wrapper me-3">
                             <i class="fas fa-chevron-right text-xxs transition-icon" :class="{'rotate-90 text-theme': expandedId === branch.id}"></i>
                          </div>
                          <p class="text-xs font-weight-bold mb-0 text-dark">{{ branch.branch_code || branch.code || branch.id }}</p>
                        </div>
                      </td>
                      <td><h6 class="mb-0 text-sm" :class="{'text-theme font-weight-bolder': expandedId === branch.id}">{{ branch.branch_name || branch.department_name || branch.name }}</h6></td>
                      <td v-if="!isMedicalType"><p class="text-xs mb-0">{{ branch.city }}</p></td>
                      <td>
                        <!-- If multiple doctors are returned -->
                        <div v-if="(branch.doctors && branch.doctors.length > 0) || getAssignedDoctors(branch).length > 0" class="d-flex flex-wrap gap-1" style="max-width: 250px;">
                          <span 
                            v-for="doc in (branch.doctors && branch.doctors.length > 0 ? branch.doctors : getAssignedDoctors(branch))" 
                            :key="doc.id" 
                            class="badge bg-light text-dark border text-xxs px-2 py-1"
                          >
                            {{ doc.name }}
                          </span>
                        </div>
                        <!-- Fallback: doctor_names string returned by backend -->
                        <span v-else-if="branch.doctor_names" class="text-xs text-dark font-weight-bold">
                          {{ branch.doctor_names }}
                        </span>
                        <!-- Fallback: contact_person if legacy -->
                        <span v-else-if="branch.contact_person" class="badge bg-light text-dark border text-xxs px-2 py-1">
                          {{ branch.contact_person }}
                        </span>
                        <span v-else class="text-xs text-muted font-italic">
                          No doctors assigned
                        </span>
                      </td>
                      <td v-if="!isMedicalType"><p class="text-xs mb-0 font-weight-bold text-dark">{{ branch.role || '-' }}</p></td>
                      <td>
                        <span class="badge badge-sm" :class="branch.status ? 'bg-theme' : 'bg-gradient-secondary'">
                          {{ branch.status ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td class="align-middle text-center">
                        <div class="d-flex justify-content-center align-items-center">
                          <button class="btn btn-link text-theme btn-sm mb-0 py-0 font-weight-bold" @click.stop="toggleExpand(branch.id)">
                            <i class="fas fa-eye me-1"></i> VIEW
                          </button>
                          <div class="vr mx-2 my-2" style="height: 15px; background-color: #ddd;"></div>
                          <button class="btn btn-link text-danger btn-sm mb-0 py-0 font-weight-bold" @click.stop="deleteBranch(branch.id)">
                            <i class="fas fa-trash-alt me-1"></i> DELETE
                          </button>
                        </div>
                      </td>
                    </tr>

                    <tr v-if="expandedId === branch.id">
                      <td colspan="6" class="p-0 border-0">
                        <div class="expanded-wrapper mx-2 mx-md-3 mb-4 mt-2">
                          <div class="d-flex justify-content-between align-items-center mb-2 px-2">
                            <span class="text-xxs font-weight-bolder text-secondary text-uppercase ls-1">Contact Details</span>
                          </div>
                          <div class="contact-card bg-white border rounded-3 p-3 shadow-none d-flex flex-column flex-md-row align-items-start align-items-md-center mb-2 gap-3">
                            <div class="col-12 col-md-3">
                                <h6 class="text-sm font-weight-bold mb-0 text-dark">{{ branch.contact_person || 'N/A' }}</h6>
                                <p class="text-xs text-secondary mb-0">{{ branch.email || 'No Email' }}</p>
                            </div>
                            <div class="col-12 col-md-3 text-start text-md-center">
                                <span class="text-xxs text-secondary font-weight-bolder d-block mb-1">PHONE</span>
                                <p class="text-xs mb-0">{{ branch.phone || '-' }}</p>
                            </div>
                            <div class="col-12 col-md-3 text-start text-md-center">
                                <span class="text-xxs text-secondary font-weight-bolder d-block mb-1">USER ID</span>
                                <span class="badge bg-light text-dark border" style="font-size: 10px">{{ branch.user_id || 'N/A' }}</span>
                            </div>
                             <div class="col-12 col-md-3 text-start text-md-center">
                                <span class="text-xxs text-secondary font-weight-bolder d-block mb-1">ASSIGNED ROLE</span>
                                <span class="badge bg-info text-white border" style="font-size: 10px">{{ branch.role || 'N/A' }}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- 2. CARD VIEW -->
            <div v-else class="px-3 px-md-4 pb-4">
              <div class="row">
                <div v-if="filteredBranches.length === 0" class="col-12 text-center py-5 text-secondary">
                  <i class="fas fa-building fa-2x mb-2 text-secondary opacity-5 d-block"></i>
                  <p class="text-sm font-weight-bold mb-1">No {{ branchPluralLabel }} Found</p>
                  <p class="text-xs text-muted mb-0">Click "+ {{ addBranchLabel }}" above to create your first {{ branchLabel.toLowerCase() }}.</p>
                </div>
                <div v-else v-for="branch in filteredBranches" :key="'card-' + branch.id" class="col-12 col-md-6 col-xl-4 mb-4">
                  <div class="card h-100 border shadow-sm branch-display-card">
                    <div class="card-body p-3 d-flex flex-column">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="overflow-hidden">
                          <h6 class="text-theme mb-0 font-weight-bolder text-truncate">{{ branch.branch_name || branch.department_name || branch.name }}</h6>
                          <p class="text-xxs text-secondary mb-0">ID: {{ branch.branch_code || branch.code || branch.id }}</p>
                        </div>
                        <TableActions @edit="editBranch(branch)" @delete="deleteBranch(branch.id)" />
                      </div>
                      
                      <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
                        <span class="badge badge-sm" :class="branch.status ? 'bg-theme' : 'bg-gradient-secondary'">
                          {{ branch.status ? 'Active' : 'Inactive' }}
                        </span>
                        <span v-if="!isMedicalType" class="text-xxs font-weight-bold text-dark"><i class="fas fa-map-marker-alt me-1 text-theme"></i>{{ branch.city }}</span>
                      </div>

                      <div class="contact-section flex-grow-1">
                        <p class="text-xxs font-weight-bolder text-uppercase text-secondary mb-2 ls-1 border-bottom pb-1">
                          {{ isMedicalType || (doctorList && doctorList.length > 0) ? 'Assigned Doctors' : 'Primary Contact' }}
                        </p>
                        <div v-if="isMedicalType || (doctorList && doctorList.length > 0) || (branch.doctors && branch.doctors.length > 0) || branch.doctor_ids" class="mb-3">
                          <div v-if="(branch.doctors && branch.doctors.length > 0) || getAssignedDoctors(branch).length > 0" class="d-flex flex-wrap gap-1">
                            <span 
                              v-for="doc in (branch.doctors && branch.doctors.length > 0 ? branch.doctors : getAssignedDoctors(branch))" 
                              :key="doc.id" 
                              class="badge bg-light text-dark border text-xxs px-2 py-1"
                            >
                              {{ doc.name }}
                            </span>
                          </div>
                          <span v-else-if="branch.doctor_names" class="text-xs text-dark font-weight-bold">
                            {{ branch.doctor_names }}
                          </span>
                          <span v-else-if="branch.contact_person" class="badge bg-light text-dark border text-xxs px-2 py-1">
                            {{ branch.contact_person }}
                          </span>
                          <span v-else class="text-xs text-muted font-italic">
                            No doctors assigned
                          </span>
                        </div>
                        <div v-else class="mb-3 p-2 bg-light rounded-2 border">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-xs font-weight-bold text-dark">{{ branch.contact_person || 'N/A' }}</span>
                            <span class="badge bg-white text-dark border text-xxs px-2" style="font-size: 9px">{{ branch.user_id ? 'UID: ' + branch.user_id : 'No ID' }}</span>
                          </div>
                          <div class="text-xxs text-secondary">
                            <div class="d-flex align-items-center mb-1">
                              <i class="fas fa-user-shield me-2 text-theme" style="width: 10px;"></i>
                              <span>{{ branch.role || 'No Role' }}</span>
                            </div>
                            <div class="d-flex align-items-center mb-1">
                              <i class="fas fa-phone-alt me-2 text-theme" style="width: 10px;"></i>
                              <span>{{ branch.phone || '-' }}</span>
                            </div>
                            <div class="d-flex align-items-center">
                              <i class="fas fa-envelope me-2 text-theme" style="width: 10px;"></i>
                              <span class="text-truncate" style="max-width: 180px;">{{ branch.email || '-' }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. PAGINATION CONTROLS (6 records visible per page) -->
            <div v-if="filteredBranchesAll.length > 0" class="d-flex justify-content-between align-items-center flex-wrap px-4 py-3 border-top">
              <span class="text-xs text-secondary font-weight-bold">
                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredBranchesAll.length) }} of {{ filteredBranchesAll.length }} {{ branchPluralLabel.toLowerCase() }}
              </span>
              <div class="pagination flex-wrap gap-1 mb-0 ms-auto">
                <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-xs btn-outline-secondary mb-0">
                  <i class="fas fa-chevron-left me-1"></i> Prev
                </button>
                <button 
                  v-for="page in totalPages" 
                  :key="page" 
                  @click="goToPage(page)"
                  class="btn btn-xs mb-0 px-3"
                  :class="currentPage === page ? 'btn-theme-primary text-white' : 'btn-outline-secondary'"
                >
                  {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages" class="btn btn-xs btn-outline-secondary mb-0">
                  Next <i class="fas fa-chevron-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Add/Edit -->
    <div v-if="showModal" class="modal-overlay px-3">
      <div class="modal-content card shadow-lg border-0 border-radius-xl w-100">
        <div class="card-header pb-0 d-flex justify-content-between bg-transparent">
          <h6 class="font-weight-bolder text-dark mb-0">{{ isEdit ? 'Edit ' + branchLabel : addBranchLabel }}</h6>
          <button class="btn-close-theme" @click="closeModal">&times;</button>
        </div>
        <div class="card-body" style="max-height: 80vh; overflow-y: auto;">
          <div class="row">
            <div class="col-md-6 col-12">
              <div class="form-group"><label class="form-control-label">{{ nameFieldLabel }}</label><input v-model="form.branch_name" type="text" class="form-control custom-input" :placeholder="'Enter ' + nameFieldLabel" /></div>
            </div>
            <div class="col-md-6 col-12">
              <div class="form-group"><label class="form-control-label">{{ branchLabel }} Code</label><input v-model="form.branch_code" type="text" class="form-control custom-input" :placeholder="'Enter ' + branchLabel + ' Code'" /></div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 col-12">
              <div class="form-group"><label class="form-control-label">Status</label>
                <select v-model="form.status" class="form-select custom-input">
                  <option :value="1">Active</option>
                  <option :value="0">Inactive</option>
                </select>
              </div>
            </div>
            <div class="col-md-6 col-12">
               <!-- Role is now auto-filled based on User Selection -->
              <div class="form-group"><label class="form-control-label">{{ branchLabel }} Role (Auto)</label><input v-model="form.role" type="text" class="form-control custom-input" readonly disabled /></div>
            </div>
          </div>

          <div v-if="!isMedicalType" class="row">
            <div class="col-md-4 col-12">
              <div class="form-group">
                <label class="form-control-label">State</label>
                <select v-model="form.state" class="form-select custom-input" @change="onStateChange">
                  <option value="">Select State</option>
                  <option v-for="st in statesList" :key="st.isoCode" :value="st.name">{{ st.name }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-4 col-12">
              <div class="form-group">
                <label class="form-control-label">City</label>
                <select v-model="form.city" class="form-select custom-input" :disabled="!form.state">
                  <option value="">Select City</option>
                  <option v-for="ct in fetchedCities" :key="ct.name" :value="ct.name">{{ ct.name }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-4 col-12">
              <div class="form-group">
                <label class="form-control-label">Postal Code</label>
                <input v-model="form.postal_code" type="text" class="form-control custom-input" placeholder="Enter Pincode" @input="fetchLocationByZip" />
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-control-label">{{ isMedicalType ? 'Description / Details' : 'Full Address' }}</label>
            <input v-model="form.address" type="text" class="form-control custom-input" :placeholder="isMedicalType ? 'Enter department description or room allocation details' : 'Enter full address'" />
          </div>
          
          <!-- Assign Multiple Doctors -->
          <div class="mb-3">
            <label class="form-label text-xs fw-bold text-uppercase text-secondary">
              Assign Doctors to Department
            </label>
            
            <div class="border rounded p-2 bg-light" style="max-height: 180px; overflow-y: auto;">
              <div 
                v-for="doc in doctorList" 
                :key="doc.id" 
                class="form-check py-1 border-bottom"
              >
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :value="doc.id" 
                  :id="'doc-chk-' + doc.id"
                  v-model="form.doctor_ids"
                  style="cursor: pointer;"
                />
                <label 
                  class="form-check-label text-xs fw-bold text-dark mb-0 ms-1" 
                  :for="'doc-chk-' + doc.id" 
                  style="cursor: pointer;"
                >
                  {{ doc.name }}
                  <small class="text-muted ms-1" v-if="doc.speciality">({{ doc.speciality }})</small>
                </label>
              </div>
              <div v-if="!doctorList || doctorList.length === 0" class="text-xs text-muted text-center py-2">
                No active doctors found.
              </div>
            </div>
            <small class="text-muted text-xxs mt-1 d-block">
              Selected: <span class="fw-bold text-primary">{{ (form.doctor_ids || []).length }}</span> doctor(s)
            </small>
          </div>

          <div class="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
            <button class="btn btn-light btn-sm mb-0" @click="closeModal">Cancel</button>
            <button class="btn btn-theme-primary btn-sm px-4 mb-0" @click="saveDepartment">{{ isEdit ? ('Update ' + branchLabel) : ('Save ' + branchLabel) }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import TableActions from "@/components/TableActions.vue"; 
import TableSearchLimit from "@/components/TableSearchLimit.vue";
import Swal from 'sweetalert2';
import { State, City } from 'country-state-city';

export default {
  components: { TableActions, TableSearchLimit },
  data() {
    return {
      // UPDATED: Standardized role check using same cleaning logic as fetchBranches
      userRole: (localStorage.getItem('userRole') || '').toLowerCase().replace(/\s+/g, ''),
      viewMode: 'card', 
      expandedId: null, 
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL ,
      isLoading: false,
      branches: [],
      users: [], 
      roles: [], 
      doctorList: [], // Holds all available doctors
      showModal: false,
      isEdit: false,
      currentId: null,
      globalSearch: "",
      limit: 6,
      currentPage: 1,
      itemsPerPage: 6,
      filters: { branch_name: "", city: "", state: "", postal_code: "" },
      statesList: State.getStatesOfCountry('IN'), 
      fetchedCities: [],
      isPersonDropdownOpen: false, // For searchable dropdown toggle
      form: { 
        id: null,
        name: "",
        branch_name: "", 
        code: "",
        branch_code: "", 
        role: "", 
        status: 1, 
        user_id: null,
        contact_person: "", 
        phone: "", 
        email: "", 
        doctor_ids: [], // 👈 Array of selected doctor IDs [1, 2, 5]
        contacts: [], 
        address: "", 
        city: "", 
        state: "", 
        postal_code: "", 
        latitude: 18.5204, 
        longitude: 73.8567 
      }
    };
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/\s+/g, '');
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      return role.includes('superadmin') || user.type === 1 || user.role === 'SuperAdmin' || user.role_name === 'SuperAdmin';
    },
    isMedicalType() {
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }

      const candidates = [
        user.installation_type_name,
        user.installation_type,
        user.installation_name,
        user.type_name,
        user.type,
        localStorage.getItem('installationType'),
        localStorage.getItem('installation_type_name'),
        localStorage.getItem('installation_name'),
        localStorage.getItem('type_name')
      ];

      for (const item of candidates) {
        if (!item) continue;
        let val = typeof item === 'object' ? (item.name || item.title || item.installation_type_name || '') : String(item);
        val = val.toLowerCase().trim();
        if (val.includes('medical')) return true;
      }

      // Check loaded branches for installation type name
      if (this.branches && this.branches.length > 0) {
        const first = this.branches[0];
        const bName = String(first.installation_type_name || first.installation_type || '').toLowerCase();
        if (bName.includes('medical')) return true;
      }

      return false;
    },
    branchLabel() {
      return this.isMedicalType ? 'Department' : 'Branch';
    },
    branchPluralLabel() {
      return this.isMedicalType ? 'Departments' : 'Branches';
    },
    addBranchLabel() {
      return this.isMedicalType ? 'Add Department' : 'Add Branch';
    },
    nameFieldLabel() {
      return this.isMedicalType ? 'Department Name' : 'Branch Name';
    },
    filteredBranchesAll() {
      const search = (this.globalSearch || "").toLowerCase().trim();
      if (!Array.isArray(this.branches)) return [];

      return this.branches.filter(b => {
        const name = (b.branch_name || b.department_name || b.name || b.title || "").toLowerCase();
        const code = String(b.branch_code || b.department_code || b.code || b.id || "").toLowerCase();
        return !search || name.includes(search) || code.includes(search);
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredBranchesAll.length / this.itemsPerPage));
    },
    filteredBranches() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBranchesAll.slice(start, end);
    },
    operatorSupervisorUsers() {
      if (!this.users.length) return [];
      if (!this.roles.length) return this.users;
      
      // Filter roles for Doctor (Medical) vs Supervisor/Operator (Standard)
      const allowedRoleIds = this.roles
        .filter(r => {
          const name = (r.name || "").toLowerCase().trim();
          if (this.isMedicalType) {
            return name === 'doctor' || name.includes('doctor') || name === 'physician';
          }
          return name === 'supervisor' || name === 'operator' || name === 'opertor' || name === 'manager';
        })
        .map(r => r.id);

      if (allowedRoleIds.length === 0) return this.users;

      // Filter users who belong to those role IDs
      return this.users.filter(u => allowedRoleIds.includes(u.role_id)); 
    }
  },
  mounted() { 
    this.fetchDoctors();
    this.fetchDepartments(); 
    this.fetchUsers(); 
    this.fetchRoles(); 
  },
  watch: {
    globalSearch() {
      this.currentPage = 1;
    }
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    onStateChange() { this.updateCityList(); },
    updateCityList() {
      if (this.form.state) {
        const stateObj = this.statesList.find(s => s.name === this.form.state);
        if (stateObj) this.fetchedCities = City.getCitiesOfState('IN', stateObj.isoCode);
      }
    },
    async fetchBranches() {
      this.isLoading = true;
      try {
        const rawRole = localStorage.getItem('userRole') || '';
        const role = rawRole.toLowerCase().replace(/\s+/g, '');
        const userId = localStorage.getItem('id');

        let url = `${this.apiBaseUrl}/v1/admin/branches`;
        
        // If the user is an operator, restrict the API call to only their assigned branches
        if (['operator', 'opertor'].includes(role)) {
          url += `?user_id=${userId}`;
        }

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.branches || res.data?.result || []);
        this.branches = data;
      } catch (e) {
        console.error(e);
        this.branches = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUsers() {
      const res = await axios.get(`${this.apiBaseUrl}/v1/admin/candidate`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      this.users = res.data;
    },
    async fetchRoles() {
      const res = await axios.get(`${this.apiBaseUrl}/v1/admin/roles`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      this.roles = res.data;
    },

    // CRITICAL FIX: Explicitly find user ID and Role Name
    onContactPersonChange() {
      const selectedName = this.form.contact_person;
      const user = this.users.find(u => u.name === selectedName);
      if (user) {
        this.form.user_id = user.id; // Sets the ID for the database
        this.form.phone = user.phone || "";
        this.form.email = user.email || "";

        // Look up the role name from the roles array using user.role_id
        const userRole = this.roles.find(r => r.id == user.role_id);
        this.form.role = userRole ? userRole.name : "Manager";

        // Logic preservation for contacts array
        this.form.contacts = [{ 
            contact_person: user.name, 
            phone: user.phone, 
            email: user.email, 
            role: this.form.role 
        }];
      }
    },

    // New methods for Searchable Dropdown
    selectPerson(user) {
      this.form.contact_person = user.name;
      this.onContactPersonChange();
      this.isPersonDropdownOpen = false;
    },
    closeDropdownWithDelay() {
      setTimeout(() => { this.isPersonDropdownOpen = false; }, 200);
    },

    async saveBranch() {
      try {
        const id = this.form.id || this.currentId;
        const base = (this.apiBaseUrl || '/api').replace(/\/+$/, '');
        const apiUrl = base.endsWith('/api') ? base : `${base}/api`;

        const doctorIds = Array.isArray(this.form.doctor_ids) ? this.form.doctor_ids.map(Number) : [];
        const firstDoctorId = doctorIds.length > 0 ? doctorIds[0] : (this.form.user_id || null);

        let contactPerson = this.form.contact_person || '';
        let phone = this.form.phone || '';
        let email = this.form.email || '';
        if (doctorIds.length > 0 && Array.isArray(this.doctorList)) {
          const firstDoc = this.doctorList.find(d => Number(d.id) === Number(doctorIds[0]));
          if (firstDoc) {
            contactPerson = firstDoc.name;
            phone = firstDoc.phone || phone;
            email = firstDoc.email || email;
          }
        }

        const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
        let user = {};
        try { user = JSON.parse(userStr); } catch (e) { user = {}; }

        const userInstName = user.installation_type_name || user.installation_type || localStorage.getItem('installationType') || localStorage.getItem('installation_type_name');
        const instNameString = typeof userInstName === 'object' ? (userInstName.name || 'Medical') : String(userInstName || (this.isMedicalType ? 'Medical' : 'Media'));

        const payload = {
          ...this.form,
          id: id || undefined,
          name: this.form.name || this.form.branch_name,
          branch_name: this.form.branch_name || this.form.name,
          code: this.form.code || this.form.branch_code,
          branch_code: this.form.branch_code || this.form.code,
          doctor_ids: doctorIds, // 👈 Sends [1, 2, 4]
          user_id: firstDoctorId,
          contact_person: contactPerson,
          phone: phone,
          email: email,
          installation_type_name: instNameString,
          installation_type: instNameString
        };

        if (user.installation_type_id || localStorage.getItem('installation_type_id')) {
          payload.installation_type_id = Number(user.installation_type_id || localStorage.getItem('installation_type_id'));
        }

        const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        if (id) {
          await axios.put(`${apiUrl}/v1/admin/branches/update/${id}`, payload, { headers });
        } else {
          await axios.post(`${apiUrl}/v1/admin/branches/add`, payload, { headers });
        }

        this.closeModal(); 
        this.fetchDepartments(); // Refresh table list
        Swal.fire('Saved!', 'Department saved successfully.', 'success');
      } catch (err) { 
        console.error(err);
        Swal.fire('Error', err.response?.data?.message || 'Failed to save department', 'error');
      }
    },
    saveDepartment() {
      return this.saveBranch();
    },
    fetchDepartments() {
      return this.fetchBranches();
    },
    async fetchDoctors() {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = (this.apiBaseUrl || '/api').replace(/\/+$/, '');
        const apiUrl = base.endsWith('/api') ? base : `${base}/api`;

        let allUsers = [];
        try {
          const res = await axios.get(`${apiUrl}/v1/admin/users/list`, { headers });
          allUsers = res.data?.data || res.data || [];
        } catch (e1) {
          try {
            const res2 = await axios.get(`${apiUrl}/v1/admin/candidate`, { headers });
            allUsers = res2.data?.data || res2.data || [];
          } catch (e2) {
            allUsers = [];
          }
        }

        if (!Array.isArray(allUsers)) allUsers = [];

        // Filter by doctors (or show all active users)
        this.doctorList = allUsers.filter(u => {
          const roleName = String(u.role?.name || u.role || u.role_name || u.type || '').toLowerCase();
          const spec = String(u.speciality || u.department || '').toLowerCase();
          return roleName.includes('doctor') || roleName.includes('medical') || roleName.includes('physician') || u.type === 2 || Boolean(spec);
        });

        if (this.doctorList.length === 0 && allUsers.length > 0) {
          this.doctorList = allUsers;
        }
      } catch (err) {
        console.error("Error fetching doctors:", err);
      }
    },
    getAssignedDoctors(branch) {
      if (!branch) return [];
      if (Array.isArray(branch.doctors) && branch.doctors.length > 0) {
        return branch.doctors;
      }
      let ids = [];
      if (Array.isArray(branch.doctor_ids)) {
        ids = branch.doctor_ids;
      } else if (typeof branch.doctor_ids === 'string') {
        try {
          const p = JSON.parse(branch.doctor_ids);
          if (Array.isArray(p)) ids = p;
        } catch (e) {
          ids = branch.doctor_ids.split(',').map(s => s.trim());
        }
      } else if (branch.user_id) {
        ids = [branch.user_id];
      }
      if (ids.length > 0 && Array.isArray(this.doctorList)) {
        return this.doctorList.filter(d => ids.map(String).includes(String(d.id)));
      }
      return [];
    },
    async deleteBranch(id) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to delete this ${this.branchLabel.toLowerCase()}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
          let deleteSuccess = false;

          // Attempt 1: DELETE /v1/admin/branches/delete/:id
          try {
            await axios.delete(`${this.apiBaseUrl}/v1/admin/branches/delete/${id}`, { headers });
            deleteSuccess = true;
          } catch (e1) {
            // Attempt 2: PUT /v1/admin/branches/delete/:id (soft delete)
            try {
              await axios.put(`${this.apiBaseUrl}/v1/admin/branches/delete/${id}`, {}, { headers });
              deleteSuccess = true;
            } catch (e2) {
              // Attempt 3: DELETE /v1/admin/branches/:id
              await axios.delete(`${this.apiBaseUrl}/v1/admin/branches/${id}`, { headers });
              deleteSuccess = true;
            }
          }

          if (deleteSuccess) {
            Swal.fire('Deleted!', `The ${this.branchLabel.toLowerCase()} has been deleted.`, 'success');
            this.fetchBranches();
          }
        } catch (e) {
          console.error(e);
          Swal.fire('Error', `Failed to delete ${this.branchLabel.toLowerCase()}.`, 'error');
        }
      }
    },
    openAddModal() { 
      this.isEdit = false; 
      this.currentId = null; 
      this.resetForm(); 
      this.showModal = true; 
    },
    openEditModal(item) {
      this.editBranch(item);
    },
    async editBranch(item) {
      this.isEdit = true; 
      this.currentId = item.id;

      // Load existing assigned doctors (supports doctor_ids array or legacy user_id)
      let assignedDoctorIds = [];
      if (Array.isArray(item.doctor_ids)) {
        assignedDoctorIds = item.doctor_ids.map(Number);
      } else if (typeof item.doctor_ids === 'string') {
        try {
          const parsed = JSON.parse(item.doctor_ids);
          if (Array.isArray(parsed)) assignedDoctorIds = parsed.map(Number);
        } catch (e) {
          assignedDoctorIds = item.doctor_ids.split(',').map(s => Number(s.trim())).filter(Boolean);
        }
      } else if (Array.isArray(item.doctors) && item.doctors.length > 0) {
        assignedDoctorIds = item.doctors.map(d => Number(d.id));
      } else if (item.user_id) {
        assignedDoctorIds = [Number(item.user_id)];
      }

      this.form = { 
        ...item, 
        id: item.id,
        name: item.name || item.branch_name || item.department_name || '',
        branch_name: item.branch_name || item.name || item.department_name || '',
        code: item.code || item.branch_code || item.department_code || '',
        branch_code: item.branch_code || item.code || item.department_code || '',
        contact_person: item.contact_person || '',
        phone: item.phone || '',
        email: item.email || '',
        address: item.address || '',
        status: item.status ? 1 : 0,
        doctor_ids: assignedDoctorIds
      };
      this.updateCityList(); 
      this.showModal = true; 
    },
    closeModal() { this.showModal = false; this.resetForm(); },
    resetForm() {
      this.form = { 
        id: null,
        name: "",
        branch_name: "", 
        code: "",
        branch_code: "", 
        role: "", 
        status: 1, 
        user_id: null, 
        contact_person: "", 
        phone: "", 
        email: "", 
        doctor_ids: [], // reset to empty array
        contacts: [], 
        address: "", 
        city: "", 
        state: "", 
        postal_code: "", 
        latitude: 18.5204, 
        longitude: 73.8567 
      };
    },
    toggleExpand(id) { this.expandedId = this.expandedId === id ? null : id; }
  }
};
</script>

<style scoped>
/* All styles preserved as requested */
.text-theme { color: var(--bs-primary) !important; }
.bg-theme { background-color: var(--bs-primary) !important; color: white !important; }
.btn-theme-primary { background-color: var(--bs-primary) !important; border: 1px solid var(--bs-primary) !important; color: white !important; font-weight: 800 !important; text-transform: uppercase; transition: all 0.2s ease; }
.btn-theme-primary:hover { filter: brightness(0.9); transform: translateY(-1px); }
.btn-outline-theme { border: 1px solid var(--bs-primary) !important; color: var(--bs-primary) !important; background: transparent; }
.btn-outline-theme:hover { background-color: var(--bs-primary) !important; color: white !important; }
.active-row-theme { background-color: rgba(0, 0, 0, 0.03) !important; border-left: 4px solid var(--bs-primary) !important; }
.btn-close-theme { background: transparent; border: none; font-size: 24px; color: #333; opacity: 0.6; }
.btn-close-theme:hover { opacity: 1; color: var(--bs-primary); }
.custom-input { min-height: 40px; }
.custom-input:focus { border-color: var(--bs-primary) !important; box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.1) !important; }
.branch-main-row { cursor: pointer; transition: all 0.2s ease; }
.expand-icon-wrapper { width: 20px; display: flex; justify-content: center; }
.rotate-90 { transform: rotate(90deg); }
.transition-icon { transition: transform 0.2s ease; }
.contact-card { transition: all 0.2s ease; border-radius: 12px !important; }
.branch-display-card { border-radius: 14px !important; transition: transform 0.2s; }
.branch-display-card:hover { transform: translateY(-5px); }
.expanded-wrapper { background-color: #f8f9fa; padding: 1.2rem; border-radius: 1rem; }
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 1050; }
.modal-content { width: 100%; max-width: 800px; max-height: 90vh; background: #fff; }
.form-control-label { font-size: 0.75rem; font-weight: 800; color: #344767; text-transform: uppercase; letter-spacing: 0.5px; }
.ls-1 { letter-spacing: 0.5px; }
.btn-xs { padding: 0.35rem 0.7rem; font-size: 0.7rem; font-weight: 700; }
</style>